/**
 * RoyaltyPanel — live royalty ledger sourced from the middleware proxy.
 *
 * Modes:
 *   <RoyaltyPanel scope="store"  storeId={...}  />
 *   <RoyaltyPanel scope="campus" campusId={...} />
 *
 * Pulls from:
 *   GET /api/stores/{id}/royalty   (scope=store)
 *   GET /api/campuses/{id}/royalty (scope=campus — adds by_store breakdown)
 *
 * Renders: date-range picker + KPI tiles + per-store mini-table (campus only)
 * + daily ledger table. Single-file so it can drop into any tab without prop
 * drilling.
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL, formatApiError } from '../../utils/api';
import DateRangePicker, { getPresetRange } from './DateRangePicker';
import { Money, ChartLineUp, CalendarBlank, Spinner, Storefront } from '@phosphor-icons/react';

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const Tile = ({ icon: Ic, label, value, sub, accent = 'text-[#0A2540]', testid }) => (
  <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {label}
      </p>
      <Ic size={14} className={accent} weight="duotone" />
    </div>
    <p className={`mt-2 text-2xl font-bold ${accent}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      {value}
    </p>
    {sub && <p className="mt-0.5 text-[11px] text-[#78716C]">{sub}</p>}
  </div>
);

export default function RoyaltyPanel({ scope, storeId, campusId, testIdPrefix = 'royalty' }) {
  const [range, setRange] = useState(() => getPresetRange('ytd'));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (scope === 'store' && !storeId) return;
    if (scope === 'campus' && !campusId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const base = scope === 'store'
          ? `${API_URL}/api/stores/${storeId}/royalty`
          : `${API_URL}/api/campuses/${campusId}/royalty`;
        const res = await axios.get(base, {
          headers: authHeaders(),
          params: { start_date: range.start, end_date: range.end },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load royalty data'));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [scope, storeId, campusId, range.start, range.end]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const byStore = data?.by_store || [];
  const currency = summary.currency || '';

  const avgPerDay = useMemo(() => {
    const d = Number(summary.days || 0);
    if (!d) return 0;
    return Number(summary.total_royalty || 0) / d;
  }, [summary.days, summary.total_royalty]);

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-panel`}>
      <DateRangePicker value={range} onChange={setRange} />

      {loading ? (
        <div className="py-16 flex items-center justify-center"><Spinner size={20} className="animate-spin text-[#0A2540]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Tile
              icon={Money}
              label="Royalty Earned"
              value={`${fmt(summary.total_royalty)} ${currency}`}
              sub={`${summary.days || 0} active day${summary.days === 1 ? '' : 's'}`}
              testid={`${testIdPrefix}-kpi-royalty`}
            />
            <Tile
              icon={ChartLineUp}
              label="Refill Revenue"
              value={`${fmt(summary.total_amount)} ${currency}`}
              sub={`First: ${summary.first_date || '—'}  ·  Last: ${summary.last_date || '—'}`}
              testid={`${testIdPrefix}-kpi-revenue`}
            />
            <Tile
              icon={CalendarBlank}
              label="Avg Royalty / Day"
              value={`${fmt(avgPerDay)} ${currency}`}
              sub={`Range: ${range.start} → ${range.end}`}
              testid={`${testIdPrefix}-kpi-avg`}
            />
          </div>

          {scope === 'campus' && byStore.length > 0 && (
            <div className="rounded-2xl border border-[#EDE6D8] bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-[#EDE6D8] flex items-center gap-2">
                <Storefront size={14} className="text-[#0A2540]" />
                <p className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  By Store
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Store</th>
                      <th className="text-right px-4 py-2 font-semibold">Refill Revenue</th>
                      <th className="text-right px-4 py-2 font-semibold">Royalty Earned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byStore.map((s) => (
                      <tr key={s.store_id} className="border-t border-[#EDE6D8]" data-testid={`${testIdPrefix}-store-${s.store_id}`}>
                        <td className="px-4 py-2 text-[#1A1D1C]">{s.store_name || s.store_id}</td>
                        <td className="px-4 py-2 text-right font-mono text-[#1A1D1C]">{fmt(s.amount)} {s.currency}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold text-[#0A2540]">{fmt(s.royalty_amount)} {s.currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-[#EDE6D8] bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-[#EDE6D8]">
              <p className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Daily Ledger ({rows.length} rows)
              </p>
            </div>
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C] sticky top-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Date</th>
                    {scope === 'campus' && (
                      <th className="text-left px-4 py-2 font-semibold">Store</th>
                    )}
                    <th className="text-right px-4 py-2 font-semibold">Revenue</th>
                    <th className="text-right px-4 py-2 font-semibold">Rate %</th>
                    <th className="text-right px-4 py-2 font-semibold">Royalty</th>
                    <th className="text-left px-4 py-2 font-semibold">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={scope === 'campus' ? 6 : 5} className="px-4 py-10 text-center text-sm text-[#78716C]">
                        No royalty data in this range.
                      </td>
                    </tr>
                  ) : rows.map((r) => (
                    <tr key={r.id} className="border-t border-[#EDE6D8]" data-testid={`${testIdPrefix}-row-${r.id}`}>
                      <td className="px-4 py-2 text-[#1A1D1C] font-mono text-xs">{r.report_date}</td>
                      {scope === 'campus' && (
                        <td className="px-4 py-2 text-[#1A1D1C]">{r.store_name || r.store_id}</td>
                      )}
                      <td className="px-4 py-2 text-right font-mono text-[#1A1D1C]">{fmt(r.amount)} {r.currency}</td>
                      <td className="px-4 py-2 text-right font-mono text-[#78716C]">{Number(r.royalty || 0).toFixed(2)}%</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold text-[#0A2540]">{fmt(r.royalty_amount)} {r.currency}</td>
                      <td className="px-4 py-2 text-[#78716C] text-xs font-mono">{r.invoice_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
